/**
 * Which item is the selfie and which is the photo ID.
 *
 * `aws_face` is the ONLY discriminator for the selfie: it is what makes an item
 * the face we compare against. The item type is incidental — a camera capture
 * and a file upload both produce an image — and coupling to it broke the moment
 * the production config was authored differently from the seed.
 *
 * In production BOTH items are `document_upload`, so:
 *   - requiring `itemType === "camera"` for the selfie matched nothing, and
 *   - taking "the first document_upload" for the ID resolved to the selfie.
 *
 * Mirrors the server's resolution in
 * packages/api/src/services/onboarding-verify-identity.service.ts. The two must
 * agree: the client decides which submission each image is attached to, and the
 * server decides which submissions to compare.
 */

export interface IdentityCandidate {
    id: number;
    itemType?: string | null;
    aiConfig?: { verificationMethod?: string | null } | null;
}

export function resolveIdentityItems<T extends IdentityCandidate>(
    items: readonly T[]
): { selfie: T | null; document: T | null } {
    const selfie = items.find((i) => i.aiConfig?.verificationMethod === "aws_face") ?? null;
    // Anything but the selfie itself, or her face gets filed as her passport.
    const document =
        items.find((i) => i.id !== selfie?.id && i.itemType === "document_upload") ?? null;
    return { selfie, document };
}
